import { useEffect, useMemo, useState } from 'react';
import {
  PlugZap,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  Building2,
  AlertCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { clsx } from 'clsx';
import {
  mcpConnectorsApi,
  type McpGrant,
  type McpGrantStatus,
  type McpGrantSummary,
} from '@/services/api';

const STATUS_STYLES: Record<
  McpGrantStatus,
  { label: string; color: string; bg: string; icon: React.ElementType }
> = {
  active: {
    label: 'Active',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
    icon: CheckCircle2,
  },
  revoked: {
    label: 'Revoked',
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    icon: XCircle,
  },
  expired: {
    label: 'Expired',
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    icon: Clock,
  },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtRelative(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'never';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function McpConnectorsPage() {
  const [grants, setGrants] = useState<McpGrant[]>([]);
  const [summary, setSummary] = useState<McpGrantSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Each client shares one active connector, so the live grant (who created
  // it, when it was last used) is the default view; history is a click away.
  const [statusFilter, setStatusFilter] = useState<McpGrantStatus | 'all'>(
    'active',
  );

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [g, s] = await Promise.all([
        mcpConnectorsApi.list(),
        mcpConnectorsApi.summary(),
      ]);
      setGrants(g);
      setSummary(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      statusFilter === 'all'
        ? grants
        : grants.filter((g) => g.status === statusFilter),
    [grants, statusFilter],
  );

  const clientChart = useMemo(
    () => (summary?.by_client ?? []).slice(0, 12),
    [summary],
  );
  const createdChart = useMemo(
    () =>
      (summary?.created_by_day ?? []).map((d) => ({
        date: d.key.slice(5), // MM-DD
        count: d.count,
      })),
    [summary],
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <PlugZap size={22} className="text-brand-teal" />
            MCP Connectors
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Per-client Claude connectors minted from the workbench. Each grant
            scopes a Claude session to a single client&apos;s data.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
        >
          <RefreshCw size={14} className={clsx(loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Stat cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total" value={summary.total} icon={PlugZap} />
          <StatCard
            label="Active"
            value={summary.active}
            icon={CheckCircle2}
            tone="emerald"
          />
          <StatCard
            label="Revoked"
            value={summary.revoked}
            icon={XCircle}
            tone="red"
          />
          <StatCard
            label="Expired"
            value={summary.expired}
            icon={Clock}
            tone="amber"
          />
          <StatCard
            label="Used ≤7d"
            value={summary.active_last_7d}
            icon={Activity}
            tone="teal"
          />
          <StatCard
            label="Never used"
            value={summary.never_used}
            icon={AlertCircle}
            tone="slate"
          />
        </div>
      )}

      {/* Charts */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Connectors by client" icon={Building2}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={clientChart}
                layout="vertical"
                margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="key"
                  width={110}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="count" fill="#0d9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Connectors created (per day)" icon={Activity}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={createdChart}
                margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
              >
                <defs>
                  <linearGradient id="mcpCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0d9488" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#0d9488"
                  strokeWidth={2}
                  fill="url(#mcpCreated)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-navy-900 text-sm">
            Grants
            <span className="ml-2 text-xs text-slate-400 font-normal">
              ({filtered.length})
            </span>
          </h2>
          <div className="flex items-center gap-1">
            {(['all', 'active', 'revoked', 'expired'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={clsx(
                  'text-xs px-2.5 py-1 rounded-md font-medium capitalize transition-colors',
                  statusFilter === s
                    ? 'bg-navy-900 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading && grants.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Loader2
              size={28}
              className="mx-auto text-brand-teal animate-spin mb-3"
            />
            <p className="text-sm text-slate-500">Loading connectors…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <PlugZap size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">
              No connectors {statusFilter !== 'all' ? `(${statusFilter})` : ''}{' '}
              yet.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Client</th>
                  <th className="px-4 py-2.5 font-semibold">Org</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Created by</th>
                  <th className="px-4 py-2.5 font-semibold">Created</th>
                  <th className="px-4 py-2.5 font-semibold">Last used</th>
                  <th className="px-4 py-2.5 font-semibold">Grant</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => {
                  const cfg = STATUS_STYLES[g.status];
                  const StatusIcon = cfg.icon;
                  return (
                    <tr
                      key={g.grant_id}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="px-4 py-2.5 font-medium text-navy-900">
                        {g.client_uid}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{g.org_uid}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border',
                            cfg.bg,
                            cfg.color,
                          )}
                        >
                          <StatusIcon size={11} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 max-w-[180px] truncate">
                        {g.created_by || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                        {fmtDate(g.created_at)}
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-2.5 whitespace-nowrap',
                          g.last_used_at
                            ? 'text-slate-600'
                            : 'text-slate-400 italic',
                        )}
                        title={g.last_used_at ? fmtDate(g.last_used_at) : ''}
                      >
                        {fmtRelative(g.last_used_at)}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">
                        {g.grant_id}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const TONE_STYLES: Record<string, { color: string; bg: string }> = {
  navy: { color: 'text-navy-900', bg: 'bg-navy-50' },
  emerald: { color: 'text-emerald-600', bg: 'bg-emerald-50' },
  red: { color: 'text-red-600', bg: 'bg-red-50' },
  amber: { color: 'text-amber-600', bg: 'bg-amber-50' },
  teal: { color: 'text-brand-teal', bg: 'bg-brand-teal/10' },
  slate: { color: 'text-slate-500', bg: 'bg-slate-100' },
};

function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'navy',
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  tone?: keyof typeof TONE_STYLES;
}) {
  const s = TONE_STYLES[tone] ?? TONE_STYLES.navy;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <div
          className={clsx(
            'w-6 h-6 rounded-md flex items-center justify-center',
            s.bg,
            s.color,
          )}
        >
          <Icon size={13} />
        </div>
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </span>
      </div>
      <p className="text-2xl font-bold text-navy-900">{value}</p>
    </div>
  );
}

function ChartCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h2 className="font-semibold text-navy-900 text-sm mb-4 flex items-center gap-2">
        <Icon size={15} className="text-brand-teal" />
        {title}
      </h2>
      {children}
    </div>
  );
}
